import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useUploadStore } from '@/stores/uploadStore'
import { useAuth } from '@/contexts/AuthContext'

interface FileUploadZoneProps {
  onUploadComplete?: (uploadId?: string) => void
  platform?: string
  period?: string
}

export function FileUploadZone({ onUploadComplete, platform = '', period = '' }: FileUploadZoneProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const { currentUser } = useAuth()
  const { isUploading, uploadFile, error, clearError } = useUploadStore()

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      setSelectedFile(file)
      clearError()
    }
  }, [clearError])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  })

  const handleUpload = async () => {
    if (!selectedFile || !currentUser) return

    try {
      const uploadId = await uploadFile(selectedFile, period, platform, currentUser)

      if (!error) {
        setSelectedFile(null)
        onUploadComplete?.(uploadId)
      }
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary bg-primary/5'
            : selectedFile
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50'
        }`}
      >
        <input {...getInputProps()} />

        {selectedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <FileText className="h-12 w-12 text-green-500" />
            </div>
            <div>
              <p className="font-medium text-green-700 dark:text-green-400">
                {selectedFile.name}
              </p>
              <p className="text-sm text-muted-foreground">
                Redo att ladda upp
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <Upload className="h-12 w-12 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium">
                {isDragActive ? 'Släpp filen här' : 'Dra och släpp fil här'}
              </p>
              <p className="text-sm text-muted-foreground">
                eller klicka för att välja fil
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                CSV, XLS, XLSX (max 10MB)
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          className="min-w-32"
        >
          {isUploading ? 'Laddar upp...' : 'Ladda upp'}
        </Button>
      </div>
    </div>
  )
}
